import React, { useEffect, useRef } from 'react';
import { useCart } from '../context/CartContext';
import '../stylings/BucksCustomerProductModalDetails.css';

function BucksCustomerProductModalDetails({ product, onClose }) {
  const modalRef = useRef(null);
  const detailsRef = useRef(null);
  const { addToCart } = useCart();

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Handle escape key to close
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    
    // Prevent scrolling while modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [onClose]);

  // Add to cart functionality
  const handleAddToCart = () => {
    // Create a cart-ready product with quantity
    const cartProduct = {
      ...product,
      quantity: 1
    };
    
    // Add to cart using context
    addToCart(cartProduct);
    
    // Show a brief notification
    const notification = document.getElementById('cart-notification');
    if (notification) {
      notification.textContent = `${product.name} added to basket`;
      notification.classList.add('show');
      
      setTimeout(() => {
        notification.classList.remove('show');
      }, 3000);
    }

    // Keep the modal open to allow further viewing
  };

  // Get status class based on product status
  const getStatusClass = (status) => {
    if (!status) return 'in-stock';
    
    switch(status.toLowerCase()) {
      case 'out of stock':
        return 'out-of-stock';
      case 'low stock':
        return 'low-stock';
      default:
        return 'in-stock';
    }
  };

  // Format price with 2 decimal places
  const formatPrice = (price) => {
    return typeof price === 'number' ? price.toFixed(2) : '0.00';
  };

  // If no product, don't render
  if (!product) return null;

  return (
    <div className="bucks-product-modal-overlay">
      <div className="bucks-product-modal-container" ref={modalRef}>
        <button className="bucks-modal-close-btn" onClick={onClose} aria-label="Close">
          <span aria-hidden="true">×</span>
        </button>
        
        <div className="bucks-product-modal-content">
          <div className="bucks-product-modal-image">
            <img src={product.image} alt={product.name} />
          </div>
          
          <div className="bucks-product-modal-details" ref={detailsRef}>
            <div className="bucks-product-modal-header">
              <span className="bucks-product-modal-category">{product.category}</span>
              <h2 className="bucks-product-modal-title">{product.name}</h2>
              <div className={`bucks-product-modal-status ${getStatusClass(product.status)}`}>
                {product.status || 'In Stock'}
              </div>
              {product.is_out_of_season && (
                <div className="bucks-product-modal-season-badge out-of-season">
                  Out of Season
                </div>
              )}
            </div>
            
            <div className="bucks-product-modal-pricing">
              <span className="bucks-product-modal-price">${formatPrice(product.price)}</span>
              <span className="bucks-product-modal-unit">
                {product.unit === 'each' ? 'each' : `per ${product.unit || 'item'}`}
              </span>
            </div>
            
            <p className="bucks-product-modal-description">{product.description}</p>
            
            <div className="bucks-product-modal-benefits">
              <h3>Farm Fresh Benefits</h3>
              <ul>
                <li>
                  <span className="bucks-product-modal-benefit-icon">🌿</span>
                  <span className="bucks-product-modal-benefit-text">Locally grown with sustainable farming practices</span>
                </li>
                <li>
                  <span className="bucks-product-modal-benefit-icon">🌱</span>
                  <span className="bucks-product-modal-benefit-text">Harvested at peak ripeness for optimal flavor</span>
                </li>
                <li>
                  <span className="bucks-product-modal-benefit-icon">⏱️</span>
                  <span className="bucks-product-modal-benefit-text">From our fields to your table in less than 24 hours</span>
                </li>
              </ul>
            </div>
            
            {product.unit && (
              <div className="bucks-product-modal-details-list">
                <dl>
                  <dt>Unit Size</dt>
                  <dd>{product.unit}</dd>
                </dl>
              </div>
            )}
            
            <div className="bucks-product-modal-actions">
              <button 
                className="bucks-product-modal-add-to-cart" 
                onClick={handleAddToCart}
                disabled={product.status === 'Out of Stock'}
              >
                {product.status === 'Out of Stock' ? 'Out of Stock' : 'Add to Basket'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BucksCustomerProductModalDetails;